import { Box, FlexLayout, H2, H4, Icon } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import { normalizeTargetSettings } from '@deity-io/di-data-mappings';
import type { DictMapping, IPropertyList, ListMapping, Mapping } from '@deity-io/di-data-structure-editor';
import { FieldMapping } from '@deity-io/di-data-structure-editor';
import { Text } from '@deity-io/portal-ui';
import { Collapsible } from 'components/PropertyListEditor/Collapsible';
import PropertyListEditor from 'components/PropertyListEditor/PropertyListEditor';
import { MappingActionTypes, MappingContext } from 'contexts/MappingContext';
import _ from 'lodash';
import { useContext } from 'react';
import type { FC } from 'react';
import { buildPath } from '../helpers/mappingHelpers';
import EditProcessors from './EditProcessors/EditProcessors';
import FieldPath from './FieldPath';
import RelationIndicator from './RelationIndicator';

interface EditMappingRelationProps {
  locked?: boolean;
}

const filterTargetProperties = (propertyList: IPropertyList | undefined): IPropertyList => {
  if (!propertyList) {
    return {};
  }
  const output: IPropertyList = {};
  mapRecord(propertyList, (property, key) => {
    if (property.name === 'target' && 'properties' in property) {
      mapRecord(property.properties, (targetProperty, targetPropertyKey) => {
        output[targetPropertyKey] = { ...targetProperty };
      });
    }
    if (property.name === 'targetMapping') {
      output[key] = { ...property, inputControl: 'string' };
    }

    return undefined;
  });

  return output;
};

const filterSourceProperties = (propertyList: IPropertyList | undefined): IPropertyList => {
  if (!propertyList) {
    return {};
  }
  const output: IPropertyList = {};
  mapRecord(propertyList, (property, key) => {
    if (property.name === 'source' && 'properties' in property) {
      mapRecord(property.properties, (targetProperty, targetPropertyKey) => {
        output[targetPropertyKey] = { ...targetProperty };
      });
    }

    return undefined;
  });

  return output;
};

const EditMappingRelation: FC<EditMappingRelationProps> = ({ locked }) => {
  const ctx = useContext(MappingContext);
  let mappingField: ListMapping | DictMapping | FieldMapping | undefined;
  let mappingPath: Array<string> = [];

  if (ctx?.state.selected?.source && ctx.state.structureEditor) {
    const source = ctx.state.structureEditor?.fromSourceIndex(buildPath(ctx.state.selected.source));
    if (source && source.mapping) {
      mappingPath = ctx.state.structureEditor.getMappingPath(source.mapping);
      mappingField = source.mapping as ListMapping | DictMapping | FieldMapping;
    }
  } else if (ctx?.state.selected?.target && ctx.state.structureEditor) {
    const target = ctx.state.structureEditor.fromTargetIndex(buildPath(ctx.state.selected.target));
    if (target && target.mappings.length) {
      const tMap = target.mappings[0];
      mappingPath = ctx.state.structureEditor.getMappingPath(tMap);
      mappingField = tMap as ListMapping | DictMapping | FieldMapping;
    }
  }

  let edgeProperties: IPropertyBag | undefined;
  let mappingType: string | undefined;
  let propertyList: IPropertyList | undefined;
  let propertyData: IPropertyBag = {};
  let availableProcessors: Array<string> = [];
  let availableValidators: Array<string> = [];
  let processorProperties: any;
  let validatorProperties: any;
  let processorData: Array<string | IPropertyBag> = [];

  let overriddenTypeProperties: IPropertyBag = {};
  let typeProperties: any;

  if (mappingField) {
    const root = ctx?.state.structureEditor?.getMapping() as Mapping;
    propertyList = root.getProperties(mappingPath);
    propertyData = { ...mappingField.config };
    edgeProperties = { ...propertyData.edgeProperties };
    availableProcessors = [...mappingField.availableProcessors()];
    processorProperties = {};
    availableProcessors.forEach(type => {
      processorProperties[type] = mappingField?.getProcessorProperties(type);
    });
    processorData = mappingField?.config.processors || [];
    mappingType = mappingField.coreType;
    mappingType = mappingType[0].toLocaleUpperCase() + mappingType.substring(1);
    availableValidators = mappingField.availableValidators();
    mappingField.getValidatorsConfig();
    validatorProperties = {};
    availableValidators.forEach(validatorKey => {
      validatorProperties[validatorKey] = mappingField!.getValidatorProperties(validatorKey);
    });
    typeProperties = FieldMapping.isItMe(mappingField) ? mappingField.getTypeProperties() : {};

    overriddenTypeProperties = mapRecord(typeProperties, (property, key) => {
      if (
        mappingField &&
        typeof mappingField.config.type === 'object' &&
        key in mappingField.config.type[Object.keys(mappingField.config.type)[0]]
      ) {
        return property;
      }

      return undefined;
    });
  }
  const targetPropertyList = filterTargetProperties(propertyList);
  const customTargetsData = normalizeTargetSettings(propertyData);

  const sourcePropertyList = filterSourceProperties(propertyList);
  const customSourceData: IPropertyBag =
    propertyData.source && typeof propertyData.source === 'object' ? { ...propertyData.source } : {};

  const onPropertyChange = (path: Array<string>, value: any) => {
    ctx?.dispatch({
      type: MappingActionTypes.UPDATE_DEFAULT,
      payload: {
        path: mappingPath,
        property: path,
        value
      }
    });
  };

  const onTargetPropertyChange = (path: Array<string>, value: any) => {
    ctx?.dispatch({
      type: MappingActionTypes.UPDATE_TARGET_PROPERTY,
      payload: {
        path: mappingPath,
        property: path,
        value
      }
    });
  };

  const onProcessorChange = (path: Array<string>, value: any) => {
    ctx?.dispatch({
      type: MappingActionTypes.UPDATE_DEFAULT,
      payload: {
        path: mappingPath,
        property: [...path],
        value
      }
    });
  };

  return (
    <Box
      key={mappingPath.join('/')}
      p="xxl"
      css={{
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden'
      }}
    >
      <FlexLayout flexDirection="column" alignItems="flex-start">
        <FlexLayout alignItems="center">
          <Icon src="accountTreeRounded" color="primary" stroke="transparent" mr="xs" />
          <H2 fontWeight="normal">{mappingType} mapping</H2>
        </FlexLayout>
        <FlexLayout alignItems="left">
          <H4>{`/${mappingPath.join('/')}`}</H4>
        </FlexLayout>
        <FlexLayout p="xss" alignItems="center" justifyContent="center" flexWrap="nowrap">
          <RelationIndicator
            color={edgeProperties?.color || '#bebfd0'}
            onChange={color => {
              ctx?.dispatch({
                type: MappingActionTypes.UPDATE_DEFAULT,
                payload: {
                  path: mappingPath,
                  property: ['edgeProperties', 'color'],
                  value: color,
                  regenerateHash: true
                }
              });
            }}
          />
          <FlexLayout ml="lg" alignItems="center" style={{ minHeight: '140px' }}>
            <FieldPath
              title="SOURCE"
              path={_.isUndefined(ctx?.state.selected?.source) ? ['self'] : ctx?.state.selected?.source}
            />
            <FieldPath
              title="TARGET"
              path={_.isUndefined(ctx?.state.selected?.target) ? ['self'] : ctx?.state.selected?.target}
            />
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
      <Collapsible
        key="Field Settings"
        title="Field Settings"
        propertyList={propertyList}
        propertyBag={propertyData}
        itemTitle="PROPERTIES"
      >
        <Box px="xs" my="xs">
          <Text>Set the overrides for this field:</Text>
        </Box>
        {propertyList && (
          <PropertyListEditor
            propertyList={propertyList}
            data={propertyData}
            locked={locked || ctx?.state.locked}
            onChange={onPropertyChange}
            onDelete={key => {
              ctx?.dispatch({
                type: MappingActionTypes.UPDATE_DEFAULT,
                payload: {
                  path: mappingPath,
                  property: key,
                  value: undefined
                }
              });
            }}
            owner={mappingField}
          />
        )}
      </Collapsible>

      {!_.isEmpty(typeProperties) && (
        <Collapsible
          key="ype Settings"
          title="Type Settings"
          propertyBag={overriddenTypeProperties}
          propertyList={typeProperties}
          itemTitle="PROPERTIES"
        >
          <PropertyListEditor
            propertyList={typeProperties}
            data={
              typeof mappingField?.config.type === 'string'
                ? {}
                : mappingField?.config.type[Object.keys(mappingField?.config.type)[0]]
            }
            owner={mappingField}
            locked={ctx?.state.locked || locked}
            onChange={(key, value) => {
              ctx?.dispatch({
                type: MappingActionTypes.UPDATE_DEFAULT,
                payload: {
                  path: mappingPath,
                  /**
                   * @todo Object.keys(schema.config.type)[0] is type is string
                   */
                  property: ['type', Object.keys(mappingField?.config.type)[0], ...key],
                  value
                }
              });
            }}
            onDelete={key => {
              ctx?.dispatch({
                type: MappingActionTypes.UPDATE_DEFAULT,
                payload: {
                  path: mappingPath,
                  /**
                   * @todo Object.keys(schema.config.type)[0] is type is string
                   */
                  property: ['type', Object.keys(mappingField?.config.type)[0], ...key],
                  value: undefined
                }
              });
            }}
          />
        </Collapsible>
      )}

      <EditProcessors
        key="EditConstraints"
        locked={locked || ctx?.state.locked}
        availableProcessors={availableValidators}
        processorProperties={validatorProperties}
        fieldPath={mappingPath || []}
        processors={[...(mappingField?.config.validators || [])]}
        onChange={onProcessorChange}
        owner={mappingField}
        title="Constraints"
        configPath={['validators']}
      />

      <EditProcessors
        key="EditProcessors"
        locked={locked || ctx?.state.locked}
        availableProcessors={availableProcessors}
        processorProperties={processorProperties}
        fieldPath={mappingPath || []}
        processors={[...(processorData || [])]}
        onChange={onProcessorChange}
        owner={mappingField}
        title="Processors"
        configPath={['processors']}
      />

      {propertyList && (
        /**
         * Todo Fix this 'target' offset mess
         */
        <Collapsible key="customizeTarget" title="Customize Targets" countLabel={`${customTargetsData.length}`}>
          <Icon
            src="add"
            stroke="transparent"
            mr="xs"
            onClick={e => {
              ctx?.dispatch({
                type: MappingActionTypes.NEW_TARGET_PROPERTY,
                payload: {
                  path: mappingPath
                }
              });
            }}
          />
          {customTargetsData.map((targetData, targetIndex) => (
            <Collapsible
              // eslint-disable-next-line react/no-array-index-key
              key={`Target ${targetIndex}`}
              title={`Target ${targetIndex}`}
              collapsed={false}
              propertyList={targetPropertyList}
              propertyBag={targetData}
              actionButtons={[
                <Icon
                  // eslint-disable-next-line react/no-array-index-key
                  key={`delete_Target ${targetIndex}`}
                  src="delete"
                  stroke="transparent"
                  mr="xs"
                  onClick={e => {
                    ctx?.dispatch({
                      type: MappingActionTypes.REMOVE_TARGET_PROPERTY,
                      payload: {
                        index: targetIndex,
                        path: mappingPath
                      }
                    });
                  }}
                />
              ]}
            >
              <PropertyListEditor
                // eslint-disable-next-line react/no-array-index-key
                key={`Target_editor ${targetIndex}`}
                propertyList={targetPropertyList}
                data={targetData}
                locked={locked || ctx?.state.locked}
                pathPreFix={[`${targetIndex}`]}
                onChange={(path, value) => {
                  onTargetPropertyChange(path, value);
                }}
                onDelete={path => {
                  onTargetPropertyChange(path, undefined);
                }}
                owner={mappingField}
              />
            </Collapsible>
          ))}
        </Collapsible>
      )}
      {propertyList && (
        <Collapsible
          key="customizeSource"
          title="Customize Source"
          propertyList={sourcePropertyList}
          propertyBag={customSourceData}
        >
          <PropertyListEditor
            key="customizeSourceEditor"
            propertyList={sourcePropertyList}
            data={customSourceData}
            locked={locked || ctx?.state.locked}
            onChange={(path, value) => {
              if (path.length) {
                const newPath = [...path];
                const key = newPath.pop() as string;
                newPath.push('source');
                newPath.push(key);
                onPropertyChange(newPath, value);
              }
            }}
            onDelete={path => {
              const newPath = [...path];
              const key = newPath.pop() as string;
              newPath.push('source');
              newPath.push(key);
              onPropertyChange(newPath, undefined);
            }}
            owner={mappingField}
          />
        </Collapsible>
      )}
    </Box>
  );
};

export default EditMappingRelation;
